import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { procoreApi } from "apis/procore/procoreApi";
import { type RootState } from "app";
import { AsyncState } from "types/misc";

import type { Drawing, DrawingAB, Level, ModelAB, Uuid } from "./types";

export type MiniMapState = {
    levels: Level[]; // Kept sorted by elevation (ascending)
    drawings: Record<Uuid, Drawing>;
    modelABs: Record<Uuid, ModelAB>;
    drawingABs: Record<Uuid, DrawingAB>;
    currentLevelId: Uuid | null;
    currentDrawingId: Uuid | null;
    tempDrawingId: Uuid | null; // Temporary drawing selection (e.g., while browsing in LargeMapWeb)
    isLargeMapMobileOpen: boolean;
};

export type MiniMapPayload = Omit<MiniMapState, "tempDrawingId" | "isLargeMapMobileOpen">;

const initialState: MiniMapState = {
    levels: [],
    drawings: {},
    modelABs: {},
    drawingABs: {},
    currentLevelId: null,
    currentDrawingId: null,
    tempDrawingId: null,
    isLargeMapMobileOpen: false,
};

export const miniMapSlice = createSlice({
    name: "miniMap",
    initialState,
    reducers: {
        setCurrentLevel: (state, action: PayloadAction<Uuid>) => {
            state.currentLevelId = action.payload;
        },
        setCurrentDrawing: (state, action: PayloadAction<Uuid>) => {
            state.currentDrawingId = action.payload;
        },
        setDrawingData: (state, action: PayloadAction<{ drawingId: Uuid; asyncState: AsyncState<ImageBitmap> }>) => {
            const drawing = state.drawings[action.payload.drawingId];
            if (drawing) {
                drawing.drawingData.asyncState = action.payload.asyncState;
            }
        },
        setTempDrawingId: (state, action: PayloadAction<Uuid | null>) => {
            state.tempDrawingId = action.payload;
        },
        setLargeMapMobileOpen: (state, action: PayloadAction<boolean>) => {
            state.isLargeMapMobileOpen = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Mirror `getMiniMapData` cache into slice state so existing selectors
        // (`selectMiniMapLevels`, `selectCurrentDrawing`, ...) keep working
        // unchanged. Behavior is identical to the previous `setMiniMapData`
        // reducer: levels are sorted by elevation, current level/drawing are
        // taken from the payload (defaults to the first level/drawing on every
        // fetch). Per-drawing image fetching is owned by `useMiniMapData` /
        // component camera handlers via `setDrawingData`.
        //
        // Drawings, modelABs and drawingABs are shallow-cloned because the
        // RTK Query cache reference is frozen; later `setDrawingData` calls
        // mutate `state.drawings[id].drawingData.asyncState`, which would
        // throw if the object came directly from the (frozen) cache.
        builder.addMatcher(procoreApi.endpoints.getMiniMapData.matchFulfilled, (state, action) => {
            const { levels, drawings, modelABs, drawingABs, currentLevelId, currentDrawingId } = action.payload;
            state.levels = [...levels].sort((a, b) => a.elevation - b.elevation);

            const drawingsCopy: Record<Uuid, Drawing> = {};
            for (const id of Object.keys(drawings)) {
                const d = drawings[id];
                drawingsCopy[id] = { ...d, drawingData: { ...d.drawingData } };
            }
            state.drawings = drawingsCopy;
            state.modelABs = { ...modelABs };
            state.drawingABs = { ...drawingABs };
            state.currentLevelId = currentLevelId;
            state.currentDrawingId = currentDrawingId;
        });
    },
});

export const { setCurrentLevel, setCurrentDrawing, setDrawingData, setTempDrawingId, setLargeMapMobileOpen } =
    miniMapSlice.actions;

export const selectMiniMapLevels = (state: RootState) => state.miniMap.levels;
export const selectMiniMapDrawings = (state: RootState) => state.miniMap.drawings;
export const selectMiniMapModelABs = (state: RootState) => state.miniMap.modelABs;
export const selectMiniMapDrawingABs = (state: RootState) => state.miniMap.drawingABs;
export const selectCurrentLevel = (state: RootState) => state.miniMap.currentLevelId;
const selectCurrentDrawingId = (state: RootState) => state.miniMap.currentDrawingId;
export const selectTempDrawingId = (state: RootState) => state.miniMap.tempDrawingId;
export const selectIsLargeMapMobileOpen = (state: RootState) => state.miniMap.isLargeMapMobileOpen;

export const selectMiniMapDrawingIds = createSelector([selectMiniMapLevels], (levels) =>
    levels.flatMap((level) => level.drawingIds),
);

// Helper function to get a drawing with its modelAB and drawingAB
const getDrawingWithABs = (
    drawingId: Uuid | null,
    drawings: Record<Uuid, Drawing>,
    modelABs: Record<Uuid, ModelAB>,
    drawingABs: Record<Uuid, DrawingAB>,
) => {
    if (!drawingId) {
        return null;
    }
    const drawing = drawings[drawingId];
    if (!drawing) {
        return null;
    }
    const modelAB = modelABs[drawing.modelABId];
    const drawingAB = drawingABs[drawing.drawingABId];
    return {
        ...drawing,
        modelAB: modelAB || null,
        drawingAB: drawingAB || null,
    };
};

export const selectCurrentDrawing = createSelector(
    [selectCurrentDrawingId, selectMiniMapDrawings, selectMiniMapModelABs, selectMiniMapDrawingABs],
    (currentDrawingId, drawings, modelABs, drawingABs) =>
        getDrawingWithABs(currentDrawingId, drawings, modelABs, drawingABs),
);

export const selectTempDrawing = createSelector(
    [
        selectTempDrawingId,
        selectCurrentDrawingId,
        selectMiniMapDrawings,
        selectMiniMapModelABs,
        selectMiniMapDrawingABs,
    ],
    (tempDrawingId, currentDrawingId, drawings, modelABs, drawingABs) =>
        getDrawingWithABs(tempDrawingId ?? currentDrawingId, drawings, modelABs, drawingABs),
);

export const selectTempLevel = createSelector(
    [selectTempDrawingId, selectCurrentDrawingId, selectMiniMapLevels],
    (tempDrawingId, currentDrawingId, levels) => {
        const drawingId = tempDrawingId ?? currentDrawingId;
        if (!drawingId) {
            return null;
        }
        return levels.find((level) => level.drawingIds.includes(drawingId)) ?? null;
    },
);

export const miniMapReducer = miniMapSlice.reducer;
